// Global Instructions Rule Applied!
//
// Configuration → Statement Types (admin only;
// docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md section i).
//
// The documents an investor statement run produces (cfg.investor_statement_types)
// and the report that prints each one. The panel exists so the owner can switch
// the investor performance deck between v1 (no agent-written Period
// Highlights) and v2 - and the fund report likewise - without a migration.
//
// UPDATE ONLY, deliberately. The run finds each type by its code and the deck
// by its scope, so a new row would print nothing; the save refuses the code,
// the scope, the document group and the default wording. The document group is
// shown read-only (owner 2026-10-30, D2): moving a type re-files every document
// of it in every run. The Document report select appears on the two rows that
// print a document report (D1); its options come from the read, which builds
// them with the same SQL fit rule the save enforces, so nothing is filtered or
// reshaped here.
//
// A save refreshes this panel's own read only. Nothing in globalConfigs holds
// the types (they are deliberately not in get_global_configs_v3), and both run
// begins read the row live, so a switch applies to the next Generate with no
// cache refresh. An open run form picks the change up when its tab is shown
// again.
//
// Chrome copied from EmailCategoriesPanel.tsx (the newest compliant sibling
// with the same interaction): FormSection + antd Table inside it, per-field
// autosave, the inline refusal Alert and a reload after every write.
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Select, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { lookupOptionsHelp } from '../../funds/utils/lookupOptionsHelp.tsx'
import InlineTextField from './InlineTextField.tsx'
import InlineNumberField from './InlineNumberField.tsx'
import type { StatementTypeAdminRow } from '../../../state_manager/data_models/statement_types_config_model.ts'
import {
  fetchStatementTypesConfig,
  StatementTypeFields,
  updateStatementType
} from '../services/statementTypesConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const REPORT_HELP =
  'The report a run prints for this document. A change applies to the next Generate. A report with ' +
  'Period Highlights (v2) is generated only once its highlights are signed off, when the sign-off ' +
  'setting is on; a report without them (v1) is never held.'

export default function StatementTypesPanel(): React.ReactElement {
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [rows, setRows] = useState<StatementTypeAdminRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refusal, setRefusal] = useState<string | null>(null)
  // Bumped on every load and part of the inline fields' keys, so a refused
  // edit snaps back to the stored value even when the value did not change.
  const [loadVersion, setLoadVersion] = useState(0)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchStatementTypesConfig(token()))
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Failed to load the statement types'
      setRefusal(text)
      showErrorToast(text)
    } finally {
      setLoadVersion((n) => n + 1)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const activateRef = useRefreshOnActivate(load)

  /** Every write goes through here: clear the last refusal, act, reload. On
   *  failure keep the message visible AND reload, so a control the server
   *  refused snaps back to the stored value instead of showing the rejected
   *  one. */
  const run = async (action: () => Promise<void>, failure: string): Promise<boolean> => {
    setRefusal(null)
    try {
      await action()
      await load()
      return true
    } catch (e) {
      const text = e instanceof Error ? e.message : failure
      setRefusal(text)
      showErrorToast(text)
      await load()
      return false
    }
  }

  const saveField = (row: StatementTypeAdminRow, fields: StatementTypeFields, failure: string): Promise<boolean> =>
    run(() => updateStatementType(token(), row.investor_statement_types__id, fields), failure)

  const switchReport = async (row: StatementTypeAdminRow, code: string): Promise<void> => {
    const saved = await saveField(row, { document_report_code: code }, 'Failed to change the report')
    if (!saved) return
    const name = row.prog__report_options.find((o) => o.value === code)?.label ?? code
    toast.success(`"${row.investor_statement_types__label}" now prints ${name} from the next Generate.`)
  }

  const columns: ColumnsType<StatementTypeAdminRow> = [
    {
      title: 'Type',
      dataIndex: 'investor_statement_types__label',
      width: 210,
      render: (v: string, row) => (
        <div>
          <InlineTextField
            key={`${row.investor_statement_types__id}-label-${loadVersion}`}
            value={v}
            onCommit={(next) => saveField(row, { label: next }, 'Failed to rename the statement type')}
          />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.investor_statement_types__code}
          </Text>
        </div>
      )
    },
    {
      title: 'Short label',
      dataIndex: 'investor_statement_types__short_label',
      width: 180,
      render: (v: string | null, row) => (
        <InlineTextField
          key={`${row.investor_statement_types__id}-short-${loadVersion}`}
          value={v ?? ''}
          placeholder={row.investor_statement_types__label}
          onCommit={(next) => saveField(row, { short_label: next }, 'Failed to update the short label')}
        />
      )
    },
    {
      title: 'Scope',
      dataIndex: 'prog__scope_label',
      width: 140
    },
    {
      title: 'Document group',
      dataIndex: 'statement_document_groups__label',
      width: 140,
      render: (v: string | null) => v ?? <Text type='secondary'>—</Text>
    },
    {
      title: 'Display order',
      dataIndex: 'investor_statement_types__display_order',
      width: 120,
      render: (v: number | null, row) => (
        <InlineNumberField
          key={`${row.investor_statement_types__id}-order-${loadVersion}`}
          value={v}
          min={0}
          max={1000}
          onCommit={(next) => saveField(row, { display_order: next }, 'Failed to update the display order')}
        />
      )
    },
    {
      title: (
        <span>
          Document report
          <HelpTip title={REPORT_HELP} />
        </span>
      ),
      dataIndex: 'investor_statement_types__document_report_code',
      render: (v: string, row) => {
        if (!row.prog__report_editable) return <Text type='secondary'>Printed from the statement read</Text>
        return (
          <span className='flex items-center gap-1'>
            <Select
              size='small'
              aria-label={`Document report of ${row.investor_statement_types__label}`}
              style={{ width: 290 }}
              popupMatchSelectWidth={false}
              value={v}
              options={row.prog__report_options}
              onChange={(next: string) => switchReport(row, next)}
            />
            <HelpTip
              title={lookupOptionsHelp(
                null,
                row.prog__report_options.map((o) => ({ label: o.label, description: o.description }))
              )}
            />
            {row.prog__report_is_active === false && (
              <Tooltip title='Every document of this type fails at generation until another report is chosen.'>
                <Tag color='red'>Not an active report</Tag>
              </Tooltip>
            )}
          </span>
        )
      }
    },
    {
      title: 'Last changed',
      dataIndex: 'investor_statement_types__date_modified',
      width: 170,
      render: (v: string | null, row) => (
        <Text type='secondary' style={{ fontSize: 12 }}>
          {v ? `${row.prog__modified_by_name} · ${dayjs(v).format('MM/DD/YYYY')}` : row.prog__modified_by_name}
        </Text>
      )
    }
  ]

  return (
    <div ref={activateRef} className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      {refusal && <Alert type='error' showIcon closable message={refusal} onClose={() => setRefusal(null)} />}

      <FormSection
        title='Statement types'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              What each investor statement run produces, and the report that prints it
            </Text>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        <Table
          size='small'
          rowKey='investor_statement_types__id'
          pagination={false}
          dataSource={rows}
          columns={columns}
        />
        <Text type='secondary' style={{ fontSize: 12 }}>
          Types cannot be added or removed here: the run finds each by its code. A change to a document report
          applies to the next Generate. Documents already generated keep the report they were printed with and read
          Out of date until they are generated again.
        </Text>
      </FormSection>
    </div>
  )
}
